import React, { useState, useMemo, useRef, JSX } from 'react'

import { Button } from '@centivo/react-components'
import styled from 'styled-components'
import { Content } from 'src/refactor/structure'
import { Map } from 'src/refactor/blocks/Map'

import { TSearchType } from 'src/shared/types'

import { ItemData } from 'src/context/results/types'
import { ResultsListHeader } from './ResultsListHeader'
import { RlListMapContainer, RlMapContainer } from './ResultList.styled'
import { TResultListItemProps } from './types'

interface ResultListMobileProps<T extends TSearchType>
  extends TResultListItemProps {
  type?: T
  data?: ItemData[]
  view: string | null
  isMobileView: boolean
  setCurrentPage(page: number): void
  setSelectedPin(pin: string): void
  selectedPin?: string | null
  renderItem(data: ItemData): JSX.Element
}

const mapStyle = {
  height: '31.25rem',
  width: '100%',
  marginBottom: 32,
}

const List = styled.div`
  margin-top: 16px;
  padding-bottom: 24px;

  > button {
    width: 100%;
    margin: auto;
    margin-top: 24px;
  }

  > * {
    &:not(:first-child):not(button) {
      margin-top: 16px;
    }
  }
`

export const ResultListMobile = React.forwardRef<
  HTMLDivElement,
  ResultListMobileProps<TSearchType>
>(
  <T extends TSearchType>(
    props: ResultListMobileProps<T>,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const {
      view,
      isMobileView,
      setCurrentPage,
      type,
      selectedPin,
      setSelectedPin,
      data = [],
      renderItem,
    } = props
    const initialCount = data.length > 10 ? 10 : data.length
    const [itemCount, setItemCount] = useState(initialCount)
    const list = useRef(null)

    const displayedData = data.slice(0, itemCount)

    const pins = useMemo(
      () =>
        data
          .filter(({ isVirtualCareOnly }) => !isVirtualCareOnly)
          .map(result => {
            const { address1, address2, fullAddress, phone } = result
            const popupText = [
              `${address1} ${address2 ? `, ${address2}` : ''}`,
              fullAddress,
            ]
            const pinData = {
              id: result.id,
              latitude: result.lat,
              longitude: result.long,
              title: result.practiceName,
              text: popupText,
            }

            if (type === 'practitioner') {
              pinData.text.push(phone)
            }

            return pinData
          }),
      [data, type]
    )
    const onRequestMore = () =>
      setItemCount(prev => {
        const newCount = prev + 10

        return newCount > data.length ? data.length : newCount
      })
    const isListEmpty = data.length === 0
    const shouldHideMap = !pins || pins.length === 0

    return (
      <Content ref={ref}>
        <RlListMapContainer>
          <ResultsListHeader
            hideText={isMobileView}
            setCurrentPage={setCurrentPage}
            isListEmpty={isListEmpty}
            shouldHideMap={shouldHideMap}
          />
          {view === 'map' ? (
            <RlMapContainer>
              <Map
                style={mapStyle}
                pins={pins}
                selectedPin={selectedPin}
                onSelectPin={setSelectedPin}
              />
            </RlMapContainer>
          ) : (
            <List ref={list}>
              {displayedData.map(renderItem)}
              {itemCount !== data.length && (
                <Button
                  onClick={onRequestMore}
                  type="button"
                  buttonType="show-more"
                  edgeType="sharp"
                >
                  Show more
                </Button>
              )}
            </List>
          )}
        </RlListMapContainer>
      </Content>
    )
  }
)
